/**
 * VRG-719 — read the per-FIELD reasons out of an API rejection, so a form can show them
 * under the field they belong to instead of as one toast that names no field.
 *
 * The API's field contract is `details: {[field]: string[]}` — what the global
 * ValidationPipe produces (`formatValidationErrors`), and what the product use cases now
 * attach to a duplicate SKU (`details.sku`) and to a refused image write
 * (`FILE_STORAGE_UNAVAILABLE`, `details.images`). `getErrorMessage` already reads the
 * FIRST of these for a toast; this returns ALL of them, keyed for the form.
 *
 * `fieldMap` renames API keys to form-field names where they differ (the product form
 * calls `manufactured_at` `manufacture_date`). Nested details (a DTO with children) are
 * skipped rather than flattened: there is no form field to attach them to, and the
 * caller falls back to the toast. Pure, so it is unit-tested under node's test runner
 * (`test/server-field-errors.guard.spec.ts`).
 */
export interface ServerFieldError {
  field: string
  message: string
}

export const serverFieldErrors = (
  error: unknown,
  fieldMap: Record<string, string> = {},
): ServerFieldError[] => {
  const details = (error as any)?.response?.data?.details
  if (!details || typeof details !== 'object' || Array.isArray(details)) return []

  const out: ServerFieldError[] = []
  for (const [key, value] of Object.entries(details as Record<string, unknown>)) {
    const messages = Array.isArray(value) ? value : [value]
    const message = messages.find(
      (m): m is string => typeof m === 'string' && m.trim().length > 0,
    )
    if (!message) continue
    out.push({ field: fieldMap[key] ?? key, message })
  }
  return out
}
